import { useEffect, useSyncExternalStore } from 'react';
import Router from 'next/router';

const ORIGIN_STORAGE_KEY = 'task-creation-origin';

const TASK_CREATION_PATHNAME = /^\/advisor\/dashboard\/(tasks\/new|client\/[^/]+\/new-task)(\/|$)/;

const listeners = new Set<() => void>();

export function pathnameOf(url: string) {
  return url.split(/[?#]/)[0] ?? '';
}

export function isTaskCreationUrl(url: string) {
  return TASK_CREATION_PATHNAME.test(pathnameOf(url));
}

function writeOrigin(origin: string | null) {
  if (origin === null) {
    window.sessionStorage.removeItem(ORIGIN_STORAGE_KEY);
  } else {
    window.sessionStorage.setItem(ORIGIN_STORAGE_KEY, origin);
  }
  listeners.forEach((listener) => listener());
}

// Only meaningful while the tab is on a creation page: the drawer-hosted forms render on a host
// page and must keep their own exits, so a stale value from an earlier flow can't leak into them.
function readOrigin() {
  if (!isTaskCreationUrl(window.location.pathname)) return null;
  return window.sessionStorage.getItem(ORIGIN_STORAGE_KEY);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// The page the advisor was on before entering task creation in this tab, or null when the flow
// was opened directly (a typed URL, a reload outside the flow, a new tab).
export function useTaskCreationOrigin(): string | null {
  return useSyncExternalStore(subscribe, readOrigin, () => null);
}

export function useTrackTaskCreationOrigin() {
  useEffect(() => {
    if (!isTaskCreationUrl(window.location.pathname)) writeOrigin(null);

    // routeChangeStart fires before the singleton's asPath moves, so it is still the page being left.
    function handleRouteChangeStart(url: string) {
      const entering = isTaskCreationUrl(url);
      if (entering && !isTaskCreationUrl(Router.asPath)) writeOrigin(Router.asPath);
      if (!entering) writeOrigin(null);
    }

    Router.events.on('routeChangeStart', handleRouteChangeStart);
    return () => {
      Router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, []);
}

export function TaskCreationOriginTracker() {
  useTrackTaskCreationOrigin();
  return null;
}
